import React from "react";
import { FormattedMessage } from "react-intl";
import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";

import type { MachineGroup } from "@/schemas";

import type { ProductionGroupLookup } from "../../types";

export interface MachineGroupsSelectionListProps {
	readonly machineGroups: MachineGroup[];
	readonly selectedIds: string[];
	readonly machineGroupSelectable: Record<string, boolean>;
	readonly machineGroupAssignedTo: ProductionGroupLookup;
	readonly onChange: (selectedIds: string[]) => void;
}

export const MachineGroupsSelectionList: React.FC<MachineGroupsSelectionListProps> = ({
	machineGroups,
	selectedIds,
	machineGroupSelectable,
	machineGroupAssignedTo,
	onChange,
}) => {
	return (
		<Box sx={{ overflow: "auto", minHeight: 0, flex: 1 }}>
			<FormGroup>
				{machineGroups.map((machineGroup) => {
					const selectable = machineGroupSelectable[machineGroup.id] ?? false;
					const assignedTo = machineGroupAssignedTo[machineGroup.id];

					return (
						<Stack key={machineGroup.id} display="flex">
							<FormControlLabel
								control={
									<Checkbox
										checked={selectedIds.includes(machineGroup.id)}
										disabled={!selectable}
										onChange={(_, checked) => {
											if (checked) {
												onChange([...selectedIds, machineGroup.id]);
												return;
											}

											onChange(selectedIds.filter((id) => id !== machineGroup.id));
										}}
										data-testid={`production-groups-configuration-modal__checkbox__machine-group-${machineGroup.id}`}
										data-pendoid={`production-groups-configuration-modal__checkbox__machine-group-${machineGroup.alias.toLowerCase()?.replace(/\s+/g, "-")}`}
									/>
								}
								label={machineGroup.alias}
							/>
							{Boolean(assignedTo) === true && (
								<Typography
									variant="body2"
									fontStyle="italic"
									sx={{ ml: 4, mt: -1 }}
									color={selectable ? "text.primary" : "text.disabled"}
								>
									<FormattedMessage
										id="Assigned to: {alias}"
										defaultMessage="Assigned to: {alias}"
										values={{ alias: assignedTo }}
									/>
								</Typography>
							)}
							{Boolean(machineGroup.productionEnabled) && (
								<Typography variant="body2" fontStyle="italic" sx={{ ml: 4 }} color="text.disabled">
									<FormattedMessage id="Production Enabled" defaultMessage="Production Enabled" />
								</Typography>
							)}
						</Stack>
					);
				})}
			</FormGroup>
		</Box>
	);
};
